import React, { useState } from "react";
import { X, Ticket, ChevronRight, Plus, Check } from "lucide-react";
import { saveTicketType } from "../../../services/TicketingService";

const PRESETS = [
  { label: "General Admission", description: "Standard entry ticket", price: 10, quantity: 200 },
  { label: "Early Bird", description: "Discounted ticket, limited spots", price: 5, quantity: 50 },
  { label: "VIP", description: "Premium access + perks", price: 25, quantity: 20 },
  { label: "Free Entry", description: "No charge to attend", price: 0, quantity: 500 },
];

const QuickTicketSetup = ({ eventId, eventName, onClose, onDone }) => {
  const [step, setStep] = useState("choose"); // "choose" | "custom" | "saving" | "done"
  const [selected, setSelected] = useState(null); // preset index
  const [custom, setCustom] = useState({ name: "", price: "", quantity: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState([]);

  const handleSelectPreset = (i) => setSelected(i === selected ? null : i);

  const handleSavePreset = async () => {
    if (selected === null) return;
    const preset = PRESETS[selected];
    setSaving(true);
    setError(null);
    const result = await saveTicketType(eventId, null, {
      name: preset.label,
      description: preset.description,
      price: preset.price,
      totalQuantity: preset.quantity,
      isActive: true,
      saleStartDate: null,
      saleEndDate: null,
    });
    setSaving(false);
    if (result.success) {
      setSaved(prev => [...prev, preset.label]);
      setSelected(null);
      setStep("done");
    } else {
      setError("Failed to save. Try again.");
    }
  };

  const handleSaveCustom = async () => {
    const name = custom.name.trim();
    const price = parseFloat(custom.price);
    const qty = parseInt(custom.quantity);
    if (!name) { setError("Name is required."); return; }
    if (isNaN(price) || price < 0) { setError("Enter a valid price (0 for free)."); return; }
    if (isNaN(qty) || qty < 1) { setError("Enter a valid quantity."); return; }

    setSaving(true);
    setError(null);
    const result = await saveTicketType(eventId, null, {
      name,
      description: custom.description.trim(),
      price,
      totalQuantity: qty,
      isActive: true,
      saleStartDate: null,
      saleEndDate: null,
    });
    setSaving(false);
    if (result.success) {
      setSaved(prev => [...prev, name]);
      setCustom({ name: "", price: "", quantity: "", description: "" });
      setStep("done");
    } else {
      setError("Failed to save. Try again.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-grey-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary-600 rounded-xl flex items-center justify-center">
              <Ticket className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-grey-900">Set Up Tickets</p>
              <p className="text-[11px] text-grey-400 truncate max-w-[180px]">{eventName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-grey-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-grey-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">

          {/* Done state */}
          {step === "done" && (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold text-grey-900 mb-1">Tickets created!</h3>
              {saved.length > 0 && (
                <p className="text-sm text-grey-500 mb-4">
                  {saved.join(', ')} {saved.length === 1 ? 'ticket type is' : 'ticket types are'} live on your event page.
                </p>
              )}
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => { setStep("choose"); setSaved([]); }}
                  className="w-full flex items-center justify-center gap-2 py-2.5 border border-grey-200 text-grey-700 text-sm font-medium rounded-xl hover:bg-grey-50 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add another ticket type
                </button>
                <button
                  onClick={onDone}
                  className="w-full py-2.5 bg-primary-600 text-white text-sm font-bold rounded-xl hover:bg-primary-700 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {/* Choose step */}
          {step === "choose" && (
            <>
              <p className="text-sm text-grey-500 mb-4">Pick a ticket type to get started fast, or customize your own.</p>
              {error && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
              )}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {PRESETS.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => handleSelectPreset(i)}
                    className={`text-left p-3 rounded-xl border-2 transition-all ${
                      selected === i
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-grey-200 hover:border-grey-300 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-1 mb-1">
                      <p className="text-sm font-semibold text-grey-900 leading-tight">{p.label}</p>
                      {selected === i && <Check className="w-3.5 h-3.5 text-primary-600 flex-shrink-0 mt-0.5" />}
                    </div>
                    <p className="text-[11px] text-grey-500 leading-tight mb-2">{p.description}</p>
                    <p className="text-sm font-bold text-primary-600">
                      {p.price === 0 ? 'Free' : `$${p.price}`}
                    </p>
                    <p className="text-[11px] text-grey-400">{p.quantity} spots</p>
                  </button>
                ))}
              </div>

              {selected !== null && (
                <button
                  onClick={handleSavePreset}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl transition-colors mb-3 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : `Create ${PRESETS[selected].label} Ticket`}
                  {!saving && <ChevronRight className="w-4 h-4" />}
                </button>
              )}

              <button
                onClick={() => { setStep("custom"); setError(null); }}
                className="w-full py-2.5 border border-grey-200 text-grey-600 text-sm font-medium rounded-xl hover:bg-grey-50 transition-colors"
              >
                Customize my own
              </button>
            </>
          )}

          {/* Custom step */}
          {step === "custom" && (
            <>
              <button
                onClick={() => { setStep("choose"); setError(null); }}
                className="flex items-center gap-1 text-xs text-grey-500 hover:text-grey-700 mb-4 transition-colors"
              >
                ← Back to presets
              </button>
              {error && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
              )}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-grey-700 mb-1">Ticket name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={custom.name}
                    onChange={e => setCustom(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. General Admission"
                    className="w-full px-3 py-2.5 border border-grey-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-grey-700 mb-1">Price (USD) <span className="text-red-500">*</span></label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={custom.price}
                      onChange={e => setCustom(p => ({ ...p, price: e.target.value }))}
                      placeholder="0.00"
                      className="w-full px-3 py-2.5 border border-grey-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-grey-700 mb-1">Quantity <span className="text-red-500">*</span></label>
                    <input
                      type="number"
                      min="1"
                      value={custom.quantity}
                      onChange={e => setCustom(p => ({ ...p, quantity: e.target.value }))}
                      placeholder="100"
                      className="w-full px-3 py-2.5 border border-grey-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-grey-700 mb-1">Description <span className="text-grey-400 font-normal">(optional)</span></label>
                  <textarea
                    value={custom.description}
                    onChange={e => setCustom(p => ({ ...p, description: e.target.value }))}
                    rows={2}
                    placeholder="What's included with this ticket?"
                    className="w-full px-3 py-2.5 border border-grey-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>
              </div>
              <button
                onClick={handleSaveCustom}
                disabled={saving}
                className="w-full mt-4 flex items-center justify-center gap-2 py-3 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Create Ticket Type'}
                {!saving && <ChevronRight className="w-4 h-4" />}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickTicketSetup;
